import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, addDays, compareAsc } from "date-fns";
import toast from 'react-hot-toast';
import { 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { filterFlights } from 'state/helpers';
import { countFlightsByField } from './utils';
import Header from 'components/Header';
import FilterButton from 'components/FilterButton';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
  { value: "deadheadingsByDate", label: "by Date" },
  { value: "deadheadingsByDep", label: "by Port" },
  { value: "deadheadingsBySector", label: "by Sector" },
  { value: "deadheadingsByFlightReg", label: "by Flight Reg" },
  { value: "deadheadingsByACType", label: "by AC Type" },
  { value: "deadheadingsByFlightNo", label: "by Flight No" },
];

const DeadheadingsChart = () => {    
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    
    const [view, setView] = useState('deadheadingsByDate');
    const [data, setData] = useState({
        deadheadingsByDate: [],        
        deadheadingsByDep: [],
        deadheadingsBySector: [],
        deadheadingsByFlightReg: [],
        deadheadingsByACType: [],
        deadheadingsByFlightNo: [],
    });

    const [deadheadings, setDeadheadings] = useState([]);
  
    
    useEffect(() => {    
        const handleDeadheadings = async () => {
        if (!startDate || !endDate) return null; 
        
        try {
            setLoading(true);

            const url = '/legmembers/deadheadings';
            const payload = { 
                startDate: addDays(startDate, -1), 
                endDate: addDays(endDate, 1), 
            };          

            const res = await api.post(url, payload);
            
            if (res.data?.length > 0) {        
                const filtered = res.data.filter(f => {
                    const flightDateToCompare = timeMode === 'UTC' 
                        ? f.flightDate 
                        : f.flightDateLocalBase;
                
                    return (
                        compareAsc(flightDateToCompare, startDate) >= 0 &&
                        compareAsc(flightDateToCompare, endDate) <= 0
                    );
                });

                setDeadheadings(filtered);
            }
        } catch (error) {
            setError(error)
            toast.error(error?.message || "Failed to fetch crew routes!");
        } finally {
            setLoading(false);
        }
        };
        
        handleDeadheadings();
    }, [timeMode, startDate, endDate]); 
    
    const filteredJumpSeaters = useMemo(() => {
        const js = filterFlights({ flights: deadheadings, filters, timeMode });
        return js;
    }, [filters, timeMode, deadheadings]);
        
    useEffect(() => {  
        const results = {
            deadheadingsByDate: countFlightsByField(filteredJumpSeaters, timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase', 'barline', 'DHD', 'DHDAvg', 'crewCount'),
            deadheadingsByDep: countFlightsByField(filteredJumpSeaters, 'dep', 'barline', 'DHD', 'DHDAvg', 'crewCount'),
            deadheadingsBySector: countFlightsByField(filteredJumpSeaters, 'sector', 'barline', 'DHD', 'DHDAvg', 'crewCount'),
            deadheadingsByFlightReg: countFlightsByField(filteredJumpSeaters, 'flightReg', 'barline', 'DHD', 'DHDAvg', 'crewCount'),
            deadheadingsByACType: countFlightsByField(filteredJumpSeaters, 'acType', 'barline', 'DHD', 'DHDAvg', 'crewCount'),
            deadheadingsByFlightNo: countFlightsByField(filteredJumpSeaters, 'flightNo', 'barline', 'DHD', 'DHDAvg', 'crewCount'),
        };
        
        setData(results); 
    }, [timeMode, filteredJumpSeaters]); 
        
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(deadheadings)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(deadheadings.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [deadheadings]);

    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Deadheadings ${period}`;

    const chartMap = {
        deadheadingsByDate: [
            { component: <SortBarMultiLinesChart data={data.deadheadingsByDate} title={defaultTitle} bottomLegend='Date' leftLegend='Count' digit={0} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
        deadheadingsByDep: [
            { component: <SortBarMultiLinesChart data={data.deadheadingsByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Count' digit={0} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
        deadheadingsBySector: [
            { component: <SortBarMultiLinesChart data={data.deadheadingsBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='Count' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
        deadheadingsByFlightReg: [
            { component: <SortBarMultiLinesChart data={data.deadheadingsByFlightReg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Count' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
        deadheadingsByACType: [
            { component: <SortBarMultiLinesChart data={data.deadheadingsByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Count' digit={0} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
        deadheadingsByFlightNo: [
            { component: <SortBarMultiLinesChart data={data.deadheadingsByFlightNo} title={defaultTitle} bottomLegend='Flight No' leftLegend='Count' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
    }; 
    
    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header 
                    title="Deadheadings" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '8rem', sm: '9rem' } }}
                        >
                            <InputLabel id="deadheadings-view-label">View</InputLabel>
                            <Select
                                labelId="deadheadings-view-label"
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    {view in chartMap &&
                        renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
    
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}  

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default DeadheadingsChart;
